import React from "react";
import { FiMail, FiPhone, FiMapPin, FiChevronRight, FiArrowUp } from "react-icons/fi";
import { FaLinkedinIn, FaInstagram, FaFacebookF } from "react-icons/fa6";

const logo = "/img/logo.svg";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Services", href: "/#services" },
  { label: "Our Work", href: "/our-completed-projects" },
  { label: "Blog", href: "https://blog.datametricks.com/" },
  { label: "Contact", href: "/contact-us" },
];

const serviceLinks = [
  { label: "Website Development", href: "/service/website-design-development-solutions" },
  { label: "E-commerce", href: "/service/ecommerce-website-design-development-solutions" },
  { label: "Web Applications", href: "/service/custom-website-design-development-solutions" },
  { label: "UI/UX Design", href: "/service/ui-ux-design-services" },
  { label: "CMS Development", href: "/service/cms-website-design-development-solutions" },
  { label: "Maintenance", href: "/service/website-maintenance-support" },
];

const contacts = [
  { icon: FiMail, label: "Email", value: "contact@datametricks.com", href: "mailto:contact@datametricks.com", tone: "bg-[#E8F1FF] text-[#1E6BFF]" },
  { icon: FiPhone, label: "Phone", value: "+91 73039 46657", href: "tel:+917303946657", tone: "bg-[#F3E8FF] text-[#9333EA]" },
  {
    icon: FiMapPin,
    label: "Address",
    value: "A40, Ithum Tower, Sector 62, Noida, Uttar Pradesh, India",
    tone: "bg-[#E6F8EE] text-[#16A34A]",
  },
];

const socials = [
  { icon: FaLinkedinIn, label: "LinkedIn", href: "https://www.linkedin.com/company/datametricks-consulting-pvt-ltd/", cls: "bg-[#0A66C2]" },
  {
    icon: FaInstagram,
    label: "Instagram",
    href: "https://www.instagram.com/datametricks_pvt_ltd/",
    cls: "bg-[radial-gradient(circle_at_30%_110%,#FDCB52_0%,#FD5949_35%,#D6249F_60%,#285AEB_100%)]",
  },
  { icon: FaFacebookF, label: "Facebook", href: "https://www.facebook.com/people/DataMetricks-Consulting-PvtLtd/61554350043470/", cls: "bg-[#1877F2]" },
];

const isExternal = (href) => href.startsWith("http");

const LinkList = ({ title, links }) => (
  <div>
    <h3 className="text-[19px] font-semibold text-ink max-sm:text-[17px] xl:text-[20px]">{title}</h3>
    <ul className="mt-[16px] flex flex-col gap-[8px] max-sm:mt-[12px] max-sm:gap-[10px]">
      {links.map((l) => (
        <li key={l.label}>
          <a
            href={l.href}
            {...(isExternal(l.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className="group flex w-full max-w-[200px] items-center justify-between gap-[12px] text-[15px] leading-[1.5] text-slate-500 transition hover:text-[#1E6BFF] max-sm:text-[14px]"
          >
            {l.label}
            <FiChevronRight className="shrink-0 text-[16px] text-slate-400 transition group-hover:translate-x-1 group-hover:text-[#1E6BFF] max-sm:hidden" />
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const SiteFooter = () => (
  <div className="tw">
    <footer className="bg-gradient-to-b from-white to-[#FAFBFF]">
      <div className="mx-auto w-full max-w-[1600px] px-[16px] sm:px-[24px] xl:px-[72px]">
        <div className="h-px w-full bg-[#EDF0F6]" />

        {/* Phones: brand (centred), then Quick Links + Our Services side by side, then a contact card. */}
        <div className="grid grid-cols-2 gap-x-[20px] gap-y-[32px] py-[40px] sm:gap-[40px] sm:py-[48px] lg:grid-cols-[1.25fr_1fr_1fr_1.15fr] lg:gap-[32px] lg:py-[56px]">
          {/* Brand */}
          <div className="col-span-2 max-sm:text-center sm:col-span-1">
            <a href="/" aria-label="DataMetricks home" className="inline-block">
              <img src={logo} alt="DataMetricks Consulting Private Limited" className="h-[48px] w-auto" />
            </a>
            <p className="mt-[22px] max-w-[400px] text-[16px] leading-[1.65] text-slate-500 max-sm:mx-auto max-sm:mt-[14px] max-sm:text-[14.5px] xl:text-[17px]">
              Building thoughtful digital experiences through web development, user-focused design, and modern technology.
            </p>
          </div>

          <LinkList title="Quick Links" links={quickLinks} />
          <LinkList title="Our Services" links={serviceLinks} />

          {/* Contact */}
          <div className="col-span-2 max-sm:rounded-[18px] max-sm:bg-white max-sm:p-[18px] max-sm:shadow-[0_14px_36px_-24px_rgba(40,60,140,0.35)] max-sm:ring-1 max-sm:ring-[#EDF0F6] sm:col-span-1">
            <h3 className="text-[19px] font-semibold text-ink max-sm:text-[17px] xl:text-[20px]">Contact Us</h3>
            <ul className="mt-[18px] flex flex-col gap-[16px] max-sm:mt-[14px] max-sm:gap-[14px]">
              {contacts.map((c) => (
                <li key={c.label} className="flex items-start gap-[16px]">
                  <span className={`inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-[18px] max-sm:h-[40px] max-sm:w-[40px] max-sm:text-[16px] ${c.tone}`}>
                    <c.icon />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[16px] font-medium text-ink max-sm:text-[14.5px]">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="break-words text-[15px] text-slate-500 transition hover:text-[#1E6BFF] max-sm:text-[14px]">
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-[15px] leading-[1.5] text-slate-500 max-sm:text-[14px]">{c.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="max-sm:mt-[18px] max-sm:flex max-sm:items-center max-sm:justify-between max-sm:[border-top:1px_solid_#EDF0F6] max-sm:pt-[16px]">
            <h3 className="mt-[26px] text-[19px] font-semibold text-ink max-sm:mt-0 max-sm:text-[15.5px] xl:text-[20px]">Stay Connected</h3>
            <div className="mt-[14px] flex gap-[16px] max-sm:mt-0 max-sm:gap-[10px]">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className={`inline-flex h-[48px] w-[48px] items-center justify-center rounded-full text-[19px] text-white max-sm:h-[40px] max-sm:w-[40px] max-sm:text-[16px] shadow-[0_8px_18px_-8px_rgba(30,64,175,0.5)] transition hover:-translate-y-0.5 ${s.cls}`}
                >
                  <s.icon />
                </a>
              ))}
            </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col-reverse items-center justify-between gap-[16px] border-x-0 border-b-0 border-t-[1px] border-solid border-[#EDF0F6] py-[22px] sm:flex-row">
          <p className="text-center text-[15px] text-slate-500 max-sm:text-[13px] sm:text-left">
            © {new Date().getFullYear()} Datametricks. All rights reserved.
          </p>
          <div className="flex items-center">
            <a href="/Privacypolicy" className="text-[15px] text-slate-500 transition hover:text-[#1E6BFF] max-sm:text-[14px]">
              Privacy Policy
            </a>
            <span className="mx-[22px] h-[18px] w-px bg-slate-200 max-sm:mx-[14px]" />
            <a href="/Termscondition" className="text-[15px] text-slate-500 transition hover:text-[#1E6BFF] max-sm:text-[14px]">
              Terms &amp; Conditions
            </a>
            <span className="mx-[22px] hidden h-[18px] w-px bg-slate-200 sm:block" />
            <button
              type="button"
              aria-label="Back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="ml-[16px] inline-flex h-[46px] w-[46px] max-sm:h-[40px] max-sm:w-[40px] items-center justify-center rounded-full border-[1px] border-solid border-[#DCE6FA] bg-[#F3F7FF] text-[18px] text-[#1E6BFF] transition hover:bg-[#1E6BFF] hover:text-white sm:ml-0"
            >
              <FiArrowUp />
            </button>
          </div>
        </div>
      </div>
    </footer>
  </div>
);

export default SiteFooter;
